import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AddList } from "../../components/lists/AddList";
import { listFor, listsApi, type ListsView } from "../../lib/listsApi";
import { ListsBlock, listsSummary, type CustomAudience, type PixelEvent } from "./ListsBlock";
import "../../styles/lists.css";
import { useNavigate, useParams } from "react-router";
import { useNative } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { AudienceNative } from "./AudienceNative";
import {
  andList,
  AudienceSchema,
  audienceSentence,
  incomeAllowed,
  KEPT_AGE_CAP,
  placeProblem,
  placeWords,
  RELATIONSHIP_STATUSES,
  sizeBand,
  withPlace,
  type Audience,
  type Place,
  type Start,
} from "../../../shared/goals/audience";
import type { GoalConfig } from "../../../shared/goals/config";
import { metaSettings } from "../../../shared/goals/meta";
import type { Spot } from "../../../shared/goals/placements";
import { PlaceSheet, spotsPill } from "../cc/PlaceSheet";
import { shortGoal } from "../cc/text";
import { Icon, type IconName } from "../../components/ui/Icon";
import { api, ApiError } from "../../lib/api";
import { audienceApi, type AudienceCheck, type Dimension, type Reach, type SearchHit } from "../../lib/audienceApi";
import { ccApi } from "../../lib/ccApi";
import { goalsApi, type GoalView } from "../../lib/goalsApi";
import { FlowHead, type ProductRef } from "../goal/FlowHead";
import { META_STEPS } from "../goal/steps";
import { useToast } from "../goal/useToast";
import "../../styles/wizard.css";
import "../../styles/audience.css";

/**
 * The Audience step (notes/AUDIENCE.md 5.1 to 5.9): who sees the goal's ads, filled in by the plan and
 * changed by tapping. The sentence says it all; each part opens its block; Meta checks every change and
 * estimates the reach on the right. Each change is a new version of the goal, from the one shown.
 */

/** A count as Meta gives it, short, with its unit set apart: 248 M, 60 K. */
export function Count({ n }: { n: number }) {
  const [num, unit] = n >= 1e6 ? [(n / 1e6).toFixed(n >= 1e7 ? 0 : 1), "M"] : n >= 1e3 ? [String(Math.round(n / 1e3)), "K"] : [String(n), ""];
  return (
    <>
      {num}
      {unit && <span className="u">{unit}</span>}
    </>
  );
}
export const KINDS: Record<Place["kind"], string> = { country: "Country", region: "Region", city: "City", zip: "ZIP code", metro: "Metro area", pin: "Pin" };
export const START_KINDS: [Dimension | "relationship", string][] = [
  ["interest", "Interests"],
  ["behavior", "Behaviours"],
  ["workPosition", "Job titles"],
  ["workEmployer", "Employers"],
  ["workIndustry", "Industries"],
  ["income", "Household income"],
  ["relationship", "Relationship status"],
];
export const START_WORDS: Record<Start["kind"], string> = {
  interest: "Interest",
  behavior: "Behaviour",
  lifeEvent: "Life event",
  relationship: "Relationship",
  income: "Income",
  workPosition: "Job title",
  workEmployer: "Employer",
  workIndustry: "Industry",
};
const WAIT_MS = 15_000;
const WAIT_TRIES = 32;
const DEFAULT_CITY_KM = 25;
export const ageWords = (min: number, max: number) => `${min} to ${max >= 65 ? "65+" : max}`;

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** The config with this audience, for a new version. */
const withAudience = (c: GoalConfig, a: Audience): GoalConfig => ({
  ...c,
  channels: { ...c.channels, meta: { ...c.channels.meta!, settings: { ...c.channels.meta!.settings, audience: a } } },
});

/** Two of Meta's estimates for the lens: inside what is always kept, and the whole audience. */
function lensAudiences(a: Audience): Audience[] {
  const kept = AudienceSchema.parse({ places: a.places, ages: { min: Math.min(a.ages.min, KEPT_AGE_CAP), max: 65 }, lists: a.lists.filter((l) => l.out).map((l) => ({ ...l, like: false })), reach: "fence" });
  return [kept, a];
}

function useProduct(productId: string | null) {
  const [product, setProduct] = useState<ProductRef | null>(null);
  useEffect(() => {
    if (!productId) return;
    api.products.list().then(
      (r) => {
        const p = r.products.find((x) => x.id === productId);
        setProduct(p ? { id: p.id, name: p.name, logoR2Key: p.logoR2Key, coldEmail: p.coldEmail } : null);
      },
      () => setProduct(null),
    );
  }, [productId]);
  return product;
}

function Block({ id, icon, title, summary, open, flash, onToggle, children }: { id: string; icon: IconName; title: string; summary: string; open: boolean; flash: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className={`q${open ? " open" : ""}${flash ? " flash" : ""}`} id={id}>
      <button className="q-h" type="button" onClick={onToggle} aria-expanded={open}>
        <span className="ki">
          <Icon name={icon} />
        </span>
        <span className="t">
          <b>{title}</b>
          <span className="s">{summary}</span>
        </span>
        <Icon name="chev" />
      </button>
      <div className="q-b">{children}</div>
    </div>
  );
}

/** A search over Meta's own list as the owner types: what was typed, what Meta found, and whether it is still looking. */
export function useFinder(goalId: string, dimension: Dimension | "relationship") {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (dimension === "relationship") {
      setHits(RELATIONSHIP_STATUSES.filter((r) => r.name.toLowerCase().includes(q.trim().toLowerCase())).map((r) => ({ start: { kind: "relationship", id: r.id, name: r.name, size: null }, path: [] })));
      return;
    }
    if (!q.trim()) {
      setHits(null);
      setBusy(false);
      return;
    }
    setBusy(true);
    // Why: Meta takes a second or two; an answer for words since changed or cleared must not come back.
    let live = true;
    const t = setTimeout(() => {
      audienceApi.search(goalId, q.trim(), dimension).then(
        (r) => {
          if (!live) return;
          setHits(r.results);
          setError(null);
          setBusy(false);
        },
        (e) => {
          if (!live) return;
          setError(message(e));
          setBusy(false);
        },
      );
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, dimension, goalId]);
  return { q, setQ, hits, error, busy };
}

/** A search over Meta's own list, as the owner types: places, or starting points of one kind. */
function Finder({ goalId, dimension, placeholder, onPick, onClose }: { goalId: string; dimension: Dimension | "relationship"; placeholder: string; onPick: (h: SearchHit) => void; onClose: () => void }) {
  const { q, setQ, hits, error, busy } = useFinder(goalId, dimension);
  return (
    <div className="finder">
      <input autoFocus value={q} placeholder={placeholder} aria-label={placeholder} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Escape" && onClose()} />
      {(hits || error || busy) && (
        <div className="found" aria-busy={busy}>
          {busy && (
            <div className="looking">
              <span className="spin" />
              Searching Meta's list
            </div>
          )}
          {error && !busy && <div className="bad">{error}</div>}
          {hits?.map((h, i) => {
            const name = "place" in h ? h.place.name : h.start.name;
            const kind = "place" in h ? KINDS[h.place.kind] : START_WORDS[h.start.kind];
            const where = h.path.length > 1 ? h.path.slice(0, -1).join(", ") : "";
            return (
              <button type="button" key={i} onClick={() => onPick(h)}>
                <span>
                  {name}
                  {where && <span className="k"> · {where}</span>}
                </span>
                <span className="k">{kind}</span>
              </button>
            );
          })}
          {hits && hits.length === 0 && !error && !busy && <div className="bad">Meta has nothing for that.</div>}
          {dimension !== "relationship" && <div className="src">From Meta's own list, with Meta's size estimate</div>}
        </div>
      )}
    </div>
  );
}

/** What Meta's two estimates say: how many it can reach, whether it still counts, and how that fits the daily budget. */
export function lensFacts(reach: (Reach | null)[], audience: Audience, dailyCents: number | null) {
  const [kept, full] = reach;
  const start = audience.reach === "start";
  const counting = reach.some((r) => r && !r.ready) || (reach.length > 0 && reach.every((r) => r === null) && audience.places.include.length > 0);
  // Why: when Meta may go wider, how far it may go is the whole places and ages at any budget; the money is judged on where it starts.
  const band = full?.ready && full.upper && dailyCents ? sizeBand(full.upper, dailyCents) : null;
  const day = dailyCents ? `$${Math.round(dailyCents / 100).toLocaleString("en-US")} a day` : null;
  const pos = band === "few" ? 11 : band === "wide" ? 89 : band === "right" ? 50 : null;
  const titles = audience.starts.length && audience.starts.every((s) => s.kind === "workPosition") && !audience.lists.some((l) => l.like || l.start) && !audience.events.start.length && !audience.custom.start.length ? audience.starts.length : 0;
  const sub = !start ? "people who match every setting" : titles ? `people with ${titles === 1 ? "your job title" : `your ${titles} job titles`}, who Meta starts with` : "people Meta starts with";
  const places = andList(audience.places.include.map(placeWords));
  return { kept, full, start, counting, band, day, pos, sub, places };
}

function Lens({ reach, audience, dailyCents }: { reach: (Reach | null)[]; audience: Audience; dailyCents: number | null }) {
  const { kept, full, start, counting, band, day, pos, sub, places } = lensFacts(reach, audience, dailyCents);
  const R = 132;
  // Why: while Meta counts it answers a placeholder (1,000); nothing is drawn or judged from it.
  const S = full?.ready && full.upper && kept?.ready ? Math.max(10, R * Math.sqrt(Math.min(1, full.upper / Math.max(kept?.upper ?? 0, 1)))) : 0;
  return (
    <aside className="aud-side" aria-label="Who Meta can reach">
      <div className="side-stick">
        <div className="lens-h">
          <b>Who Meta can reach</b>
          <span>Meta's estimate</span>
        </div>
        <div className="lens">
          <svg viewBox="0 0 300 300" role="img" aria-label="Who Meta can reach">
            <defs>
              <radialGradient id="au-g1">
                <stop offset="0" stopColor="#0e8f8c" stopOpacity=".32" />
                <stop offset="1" stopColor="#0e8f8c" stopOpacity=".06" />
              </radialGradient>
              <radialGradient id="au-g2">
                <stop offset="0" stopColor="#a78bfa" stopOpacity=".35" />
                <stop offset="1" stopColor="#a78bfa" stopOpacity=".05" />
              </radialGradient>
            </defs>
            {start ? (
              <circle cx="150" cy="150" r={R} fill="url(#au-g1)" stroke="#0e8f8c" strokeOpacity=".7" strokeWidth="1.5" />
            ) : (
              <circle cx="150" cy="150" r={R} style={{ fill: "rgba(var(--hi),.02)", stroke: "rgba(var(--hi),.14)" }} strokeDasharray="2 4" />
            )}
            {S > 0 && <circle cx="150" cy="150" r={S} fill="url(#au-g2)" stroke="#a78bfa" strokeOpacity=".8" strokeWidth="1.5" strokeDasharray="4 3" />}
          </svg>
        </div>
        <div className="reach">
          <div className="fact start">
            <i />
            <div>
              <span className="big">
                {full?.ready && full.lower !== null && full.upper !== null ? (
                  <>
                    <Count n={full.lower} /> to <Count n={full.upper} />
                  </>
                ) : counting ? (
                  "Meta is still counting"
                ) : (
                  "..."
                )}
              </span>
              {full?.ready && <span className="sub">{sub}</span>}
            </div>
          </div>
          {start && kept?.ready && (
            <div className="fact wide">
              <i />
              <p>
                Meta may also show the ads to others aged {Math.min(audience.ages.min, KEPT_AGE_CAP)} and over in {places} who look likely to respond.
              </p>
            </div>
          )}
        </div>
        {pos !== null && day && (
          <div className="fit">
            <div className="fit-bar">
              <i style={{ left: `calc(${pos}% - 2px)` }} />
            </div>
            <div className="fit-k">
              <span>Too few</span>
              <span>Right for your budget</span>
              <span>Very wide</span>
            </div>
            <p>{band === "few" ? `Few people for ${day}. Results may cost more.` : band === "wide" ? `Very wide for ${day}.` : `Right size for ${day}.`}</p>
          </div>
        )}
      </div>
    </aside>
  );
}

export function AudienceStep() {
  const { goalId = "" } = useParams();
  const navigate = useNavigate();
  const native = useNative();
  const { toast, say } = useToast();
  const [view, setView] = useState<GoalView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aud, setAud] = useState<Audience | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set(["q-where"]));
  const [flash, setFlash] = useState<string | null>(null);
  const [finder, setFinder] = useState<"place" | "out" | "start" | null>(null);
  const [startKind, setStartKind] = useState<Dimension | "relationship">("interest");
  const [ages, setAges] = useState<[number, number] | null>(null);
  const [check, setCheck] = useState<AudienceCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [reach, setReach] = useState<(Reach | null)[]>([]);
  const [lists, setLists] = useState<ListsView | null>(null);
  const [adding, setAdding] = useState(false);
  const version = useRef(0);
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  const load = useCallback(async () => {
    try {
      const v = await goalsApi.get(goalId);
      setView(v);
      version.current = v.current?.version ?? 0;
      const s = metaSettings(v.current?.config.channels.meta?.settings);
      setAud(s.audience ?? AudienceSchema.parse({ places: { include: [] } }));
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, [goalId]);
  useEffect(() => {
    void load();
  }, [load]);
  const [pixelEvents, setPixelEvents] = useState<PixelEvent[] | null>(null);
  const [custom, setCustom] = useState<CustomAudience[] | null>(null);
  const [unread, setUnread] = useState<("events" | "custom")[]>([]);
  useEffect(() => {
    setUnread([]);
    const miss = (k: "events" | "custom") => setUnread((u) => [...u, k]);
    audienceApi.events(goalId).then(
      (r) => (r.events ? setPixelEvents(r.events) : miss("events")),
      () => miss("events"),
    );
    audienceApi.custom(goalId).then(
      (r) => (r.audiences ? setCustom(r.audiences) : miss("custom")),
      () => miss("custom"),
    );
  }, [goalId]);
  const loadLists = useCallback(() => listsApi.forGoal(goalId).then(setLists, () => setLists({ lists: [], events: [], pixel: false })), [goalId]);
  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  const product = useProduct(view?.goal.productId ?? null);
  const config = view?.current?.config ?? null;
  // Why: once the goal has a campaign, who sees the ads changes on Meta first, through the goal's chat (specs/formal/audience-change).
  const editable = view?.goal.state === "getting_ready" && !view.goal.campaign;
  const [placing, setPlacing] = useState(false);
  const chosenSpots = view?.current ? (metaSettings(view.current.config.channels.meta?.settings).placements ?? null) : null;
  /** Where the ads show, saved in the same queue as the audience so each change builds on the last. */
  const saveSpots = (next: Spot[] | null): Promise<boolean> => {
    const run = queue.current.then(async () => {
      try {
        const r = await ccApi.placements(goalId, next);
        if (r.outcome === "failed" || r.outcome === "refused") {
          say(r.result && typeof r.result.error === "string" ? r.result.error : "It could not run.", false);
          return false;
        }
        if (r.state === "approved") say(r.result && typeof r.result.waiting === "string" ? r.result.waiting : "It runs in a few minutes.");
        else say(next ? `Saved. The ads show only in the ${next.length} spots you chose.` : "Saved. Meta picks the spots.");
        await load();
        return true;
      } catch (e) {
        say(message(e), false);
        return false;
      }
    });
    queue.current = run;
    return run;
  };
  const dailyCents = config ? (metaSettings(config.channels.meta?.settings).dailyCents ?? config.money.dailyMaxCents) : null;

  const askMeta = useCallback(
    async (fresh = false) => {
      setChecking(true);
      try {
        setCheck((await audienceApi.check(goalId, fresh)).check);
      } catch (e) {
        setCheck({ ok: false, message: message(e), at: new Date().toISOString() });
      } finally {
        setChecking(false);
        // Why: Meta's check makes what the ticks need (people like a list, the pixel's people); show where each stands.
        void loadLists();
      }
    },
    [goalId, loadLists],
  );

  // Why: Meta's estimate can come back "still counting"; ask again every 3 seconds, for up to 30.
  useEffect(() => {
    if (!aud || !aud.places.include.length) {
      setReach([]);
      return;
    }
    let stop = false;
    const asks = lensAudiences(aud);
    const run = async (tries: number) => {
      const got = await Promise.all(asks.map((a) => audienceApi.reach(goalId, a).then((r) => r.reach, () => null)));
      if (stop) return;
      setReach(got);
      if (got.some((r) => r && !r.ready) && tries < 10) setTimeout(() => void run(tries + 1), 3000);
    };
    const t = setTimeout(() => void run(0), 400);
    return () => {
      stop = true;
      clearTimeout(t);
    };
  }, [aud, goalId]);

  useEffect(() => {
    if (view?.current) void askMeta();
  }, [view, askMeta]);

  // Why: an audience it uses (a lookalike, a pixel event's people) takes Meta about a minute; ask again until Meta has it.
  const waits = useRef(0);
  useEffect(() => {
    if (!check?.waiting) {
      waits.current = 0;
      return;
    }
    if (waits.current >= WAIT_TRIES) return;
    const t = setTimeout(() => {
      waits.current += 1;
      void askMeta();
    }, WAIT_MS);
    return () => clearTimeout(t);
  }, [check, askMeta]);

  /** Save the audience as the goal's next version, one change at a time, then ask Meta to check it. */
  const save = (next: Audience, summary: string) => {
    if (!config) return;
    setAud(next);
    queue.current = queue.current.then(async () => {
      try {
        const r = await ccApi.apply(goalId, version.current, withAudience(config, next), summary);
        version.current = r.version;
        await askMeta();
      } catch (e) {
        say(message(e), false);
        await load();
      }
    });
  };

  const jump = (part: string) => {
    const id = part === "places" ? "q-where" : part === "lists" ? "q-lists" : part === "reach" ? "au-reach" : "q-who";
    setOpen((o) => new Set([...o, id]));
    setFlash(id);
    setTimeout(() => setFlash(null), 900);
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" }));
  };
  const toggle = (id: string) => setOpen((o) => (o.has(id) ? new Set([...o].filter((x) => x !== id)) : new Set([...o, id])));

  const sentence = useMemo(
    () => (aud ? audienceSentence(aud, { listNames: (id) => (lists ? listFor(lists.lists, id)?.name.toLowerCase().replace(/^/, "your ") : undefined) }) : []),
    [aud, lists],
  );

  if (native && (!view || !aud || !config))
    return (
      <div className="pn-screen ad-scr">
        <PhoneNav title="Audience" large back={{ to: `/goals/${goalId}`, label: "Goal" }} />
        <p className="pn-empty">{error && !view ? error : "Loading who sees the ads…"}</p>
      </div>
    );
  if (error && !view) return <div className="wrap ga gw au"><p className="load-note">{error}</p></div>;
  if (!view || !aud || !config) return <div className="wrap ga gw au"><p className="load-note">Loading who sees the ads…</p></div>;

  const addPlace = (h: SearchHit, out: boolean) => {
    if (!("place" in h)) return;
    const p: Place = h.place.kind === "city" ? { ...h.place, radiusKm: DEFAULT_CITY_KM } : h.place;
    if (out) {
      if (aud.places.exclude.some((x) => x.key === p.key)) return;
      save({ ...aud, places: { ...aud.places, exclude: [...aud.places.exclude, p] } }, `Who sees the ads: leave out ${p.name}`);
    } else {
      const r = withPlace(aud.places.include, p);
      if (r.replaced.length) say(`${p.name} replaces ${r.replaced.map((x) => x.name).join(" and ")}.`);
      save({ ...aud, places: { ...aud.places, include: r.places } }, `Who sees the ads: add ${p.name}`);
    }
    setFinder(null);
  };
  const removePlace = (p: Place, out: boolean) => {
    const list = out ? aud.places.exclude : aud.places.include;
    const rest = list.filter((x) => x !== p);
    save({ ...aud, places: out ? { ...aud.places, exclude: rest } : { ...aud.places, include: rest } }, `Who sees the ads: remove ${p.name}`);
  };
  const setRadius = (p: Place, km: number) => {
    const next = { ...p, radiusKm: km };
    const why = placeProblem(next);
    if (why) return say(why, false);
    save({ ...aud, places: { ...aud.places, include: aud.places.include.map((x) => (x === p ? next : x)) } }, `Who sees the ads: ${km} km around ${p.name}`);
  };
  const addStart = (h: SearchHit) => {
    if (!("start" in h)) return;
    if (aud.starts.some((s) => s.kind === h.start.kind && s.id === h.start.id)) return setFinder(null);
    const starts = h.start.kind === "income" ? [...aud.starts.filter((s) => s.kind !== "income"), h.start] : [...aud.starts, h.start];
    save({ ...aud, starts }, `Who sees the ads: start with ${h.start.name}`);
    setFinder(null);
  };
  const removeStart = (s: Start) => save({ ...aud, starts: aud.starts.filter((x) => x !== s) }, `Who sees the ads: remove ${s.name}`);
  const shownAges = ages ?? [aud.ages.min, aud.ages.max];
  const saveAges = () => {
    if (!ages) return;
    const [min, max] = ages;
    setAges(null);
    if (min !== aud.ages.min || max !== aud.ages.max) save({ ...aud, ages: { min, max } }, `Who sees the ads: aged ${min} to ${max >= 65 ? "65+" : max}`);
  };
  const lo = ((shownAges[0] - 18) / 47) * 100;
  const hi = ((shownAges[1] - 18) / 47) * 100;
  const floor = Math.min(shownAges[0], KEPT_AGE_CAP);
  const placesSummary = `${aud.places.include.map((p) => p.name).join(", ") || "Nowhere yet"}${aud.places.exclude.length ? `, not ${aud.places.exclude.map((p) => p.name).join(", ")}` : ""}`;
  const whoSummary = `${ageWords(aud.ages.min, aud.ages.max)}${aud.starts.length ? `, starting with ${aud.starts.map((s) => s.name).join(", ")}` : ""}`;
  const pill = checking
    ? ["live", "Meta is checking it"]
    : check?.ok
      ? ["good", "Meta checked it"]
      : check?.waiting
        ? ["live", "Meta is making your audiences"]
        : check
          ? ["tight", "Meta did not take it"]
          : ["plain", "Not checked yet"];

  if (native)
    return (
      <>
        <AudienceNative
          goalId={goalId}
          sentence={view.goal.sentence}
          aud={aud}
          editable={editable}
          setup={view.goal.state === "getting_ready" && !view.goal.campaign}
          pill={pill as [string, string]}
          checkMessage={!checking && check && !check.ok && check.message ? check.message : null}
          pieces={sentence}
          reach={reach}
          dailyCents={dailyCents}
          lists={lists}
          pixelEvents={pixelEvents}
          custom={custom}
          unread={unread}
          save={save}
          addPlace={addPlace}
          removePlace={removePlace}
          setRadius={setRadius}
          addStart={addStart}
          removeStart={removeStart}
          placesSummary={placesSummary}
          whoSummary={whoSummary}
          spots={chosenSpots}
          saveSpots={saveSpots}
          onAddList={() => setAdding(true)}
          toast={toast}
        />
        <AddList productId={view.goal.productId} productName={product?.name ?? "this product"} mode={adding ? { kind: "new" } : null} onClose={() => setAdding(false)} onDone={() => void loadLists()} />
      </>
    );

  return (
    <div className="wrap ga gw au">
      <FlowHead
        back={{ to: `/goals/${goalId}`, label: "Goal" }}
        crumb="New goal · Meta ads"
        product={product}
        steps={META_STEPS}
        step={5}
        canGo={(i) => i >= 4}
        onGo={(i) => navigate(i === 4 ? `/goals/${goalId}/plan` : i === 6 ? `/goals/${goalId}/ads` : `/goals/${goalId}/audience`)}
        title="Audience"
        goalRef={view.goal.sentence}
      />
      <section className="card aud" aria-labelledby="aud-h">
        <div className="aud-h">
          <div>
            <h2 id="aud-h">Who sees your ads</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <span className={`pill ${pill[0]}`}>{pill[1]}</span>
            {!checking && check && !check.ok && check.message && <span className="aud-msg">{check.message}</span>}
          </div>
        </div>
        <div className="aud-body">
          <div className="aud-main">
            <p className="sentence">
              {sentence.map((x, i) =>
                "part" in x ? (
                  <span key={i} className={`w${x.look === "start" ? " soft" : ""}`} role="button" tabIndex={0} onClick={() => jump(x.part)} onKeyDown={(e) => e.key === "Enter" && jump(x.part)}>
                    {x.text}
                  </span>
                ) : (
                  <span key={i}>{x.text}</span>
                ),
              )}
            </p>
            <div className="legend">
              <span>
                <i />
                Always kept
              </span>
              <span>
                <i className="soft" />
                Who Meta starts with, and may go wider
              </span>
            </div>

            <Block id="q-where" icon="target" title="Where" summary={placesSummary} open={open.has("q-where")} flash={flash === "q-where"} onToggle={() => toggle("q-where")}>
              <div className="lbl">
                <span className="tag hard">Always kept</span>Show the ads to people in
              </div>
              <div className="chips">
                {aud.places.include.map((p) => (
                  <span className="c" key={`${p.kind}:${p.key ?? p.name}`}>
                    {placeWords({ ...p, radiusKm: undefined })}
                    {p.kind === "city" && (
                      <select aria-label={`Distance around ${p.name}`} value={p.radiusKm ?? DEFAULT_CITY_KM} disabled={!editable} onChange={(e) => setRadius(p, Number(e.target.value))}>
                        {[17, 25, 40, 60, 80].map((km) => (
                          <option key={km} value={km}>
                            +{km} km
                          </option>
                        ))}
                      </select>
                    )}
                    <span className="m">{KINDS[p.kind]}</span>
                    {editable && (
                      <button className="x" type="button" aria-label={`Remove ${p.name}`} onClick={() => removePlace(p, false)}>
                        <Icon name="x" />
                      </button>
                    )}
                  </span>
                ))}
                {editable && (
                  <button className="add" type="button" onClick={() => setFinder(finder === "place" ? null : "place")}>
                    <Icon name="plus" />
                    Add a place
                  </button>
                )}
              </div>
              {finder === "place" && <Finder goalId={goalId} dimension="geo" placeholder="A city, a region, a country or a ZIP code" onPick={(h) => addPlace(h, false)} onClose={() => setFinder(null)} />}
              <div className="lbl">
                <span className="tag hard">Always kept</span>Leave out
              </div>
              <div className="chips">
                {aud.places.exclude.map((p) => (
                  <span className="c out" key={`${p.kind}:${p.key ?? p.name}`}>
                    {p.name} <span className="m">{KINDS[p.kind]}</span>
                    {editable && (
                      <button className="x" type="button" aria-label={`Stop leaving out ${p.name}`} onClick={() => removePlace(p, true)}>
                        <Icon name="x" />
                      </button>
                    )}
                  </span>
                ))}
                {editable && (
                  <button className="add" type="button" onClick={() => setFinder(finder === "out" ? null : "out")}>
                    <Icon name="plus" />
                    Leave out a place
                  </button>
                )}
              </div>
              {finder === "out" && <Finder goalId={goalId} dimension="geo" placeholder="A place to leave out" onPick={(h) => addPlace(h, true)} onClose={() => setFinder(null)} />}
              {aud.why.places && (
                <p className="why">
                  <b>Why these places:</b> {aud.why.places}
                </p>
              )}
            </Block>

            <Block id="q-who" icon="users" title="Who" summary={whoSummary} open={open.has("q-who")} flash={flash === "q-who"} onToggle={() => toggle("q-who")}>
              <div className="ages">
                <div className="ages-top">
                  <b>{ageWords(shownAges[0], shownAges[1])}</b>
                  <span>
                    {aud.reach === "start"
                      ? `Always kept: ${floor} and over. Meta starts with ${ageWords(shownAges[0], shownAges[1])} and may go wider.`
                      : `Only ${ageWords(shownAges[0], shownAges[1])}.`}
                  </span>
                </div>
                <div className="range">
                  <span className="track" />
                  <span className={`fill${aud.reach === "start" ? " softmax" : ""}`} style={{ left: `${lo}%`, width: `${hi - lo}%` }} />
                  <input
                    type="range"
                    min={18}
                    max={65}
                    value={shownAges[0]}
                    aria-label="Youngest"
                    disabled={!editable}
                    onChange={(e) => setAges([Math.min(Number(e.target.value), shownAges[1]), shownAges[1]])}
                    onPointerUp={saveAges}
                    onKeyUp={saveAges}
                  />
                  <input
                    type="range"
                    min={18}
                    max={65}
                    value={shownAges[1]}
                    aria-label="Oldest"
                    disabled={!editable}
                    onChange={(e) => setAges([shownAges[0], Math.max(Number(e.target.value), shownAges[0])])}
                    onPointerUp={saveAges}
                    onKeyUp={saveAges}
                  />
                </div>
              </div>
              <div className="lbl">
                <span className="tag soft">Who Meta starts with</span>People who are
              </div>
              <div className="chips">
                {aud.starts.map((s) => (
                  <span className="c soft" key={`${s.kind}:${s.id}`}>
                    {s.name} <span className="m">{START_WORDS[s.kind]}</span>
                    {editable && (
                      <button className="x" type="button" aria-label={`Remove ${s.name}`} onClick={() => removeStart(s)}>
                        <Icon name="x" />
                      </button>
                    )}
                  </span>
                ))}
                {editable && (
                  <button className="add" type="button" onClick={() => setFinder(finder === "start" ? null : "start")}>
                    <Icon name="plus" />
                    Add a starting point
                  </button>
                )}
              </div>
              {finder === "start" && (
                <div className="fd">
                  <select aria-label="Kind of starting point" value={startKind} onChange={(e) => setStartKind(e.target.value as Dimension | "relationship")}>
                    {START_KINDS.filter(([k]) => k !== "income" || incomeAllowed(aud)).map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                  </select>
                  <div style={{ flex: 1 }}>
                    <Finder key={startKind} goalId={goalId} dimension={startKind} placeholder="An interest, a job title or a habit" onPick={addStart} onClose={() => setFinder(null)} />
                  </div>
                </div>
              )}
              {(aud.why.ages || aud.why.starts) && (
                <p className="why">
                  <b>Why these:</b> {[aud.why.ages, aud.why.starts].filter(Boolean).join(" ")}
                </p>
              )}
            </Block>

            <Block id="q-lists" icon="heart" title="Client lists" summary={listsSummary(aud, lists)} open={open.has("q-lists")} flash={flash === "q-lists"} onToggle={() => toggle("q-lists")}>
              <ListsBlock aud={aud} view={lists} events={pixelEvents} custom={custom} unread={unread} editable={editable} save={save} onAdd={() => setAdding(true)} />
            </Block>

            <div className="lbl" style={{ marginTop: 4 }} id="au-reach">
              How far Meta may look
            </div>
            <div className="strict">
              {(
                [
                  ["start", "Let Meta find more people like these", "Places, a youngest age up to 25, and people left out stay fixed.", true],
                  ["fence", "Only the people I chose", "Ages and lists stay fixed too. Often costs more per result.", false],
                ] as const
              ).map(([id, t, p, rec]) => (
                <button
                  key={id}
                  type="button"
                  className={`st${aud.reach === id ? " on" : ""}`}
                  aria-pressed={aud.reach === id}
                  disabled={!editable}
                  onClick={() => aud.reach !== id && save({ ...aud, reach: id }, `Who sees the ads: ${t.toLowerCase()}`)}
                >
                  <b>
                    {t}
                    {rec && <span className="rec">Meta suggests</span>}
                  </b>
                  <p>{p}</p>
                </button>
              ))}
            </div>
            {aud.why.reach && (
              <p className="why">
                <b>Why:</b> {aud.why.reach}
              </p>
            )}
          </div>
          <Lens reach={reach} audience={aud} dailyCents={dailyCents} />
        </div>
      </section>
      <section className="card place-row">
        <span className="eyebrow">Where the ads show</span>
        <span className="pill live">{spotsPill(chosenSpots)}</span>
        {editable && (
          <button type="button" className="btn sm ghost" onClick={() => setPlacing(true)}>
            Change
          </button>
        )}
      </section>
      <PlaceSheet open={placing} onClose={() => setPlacing(false)} title={shortGoal(view.goal.sentence)} ads={0} onMeta={false} spots={chosenSpots} onSave={saveSpots} />
      <div className="wz-nav">
        <button type="button" className="btn" onClick={() => navigate(`/goals/${goalId}/ads`)}>
          Next: ads
          <Icon name="right" />
        </button>
      </div>
      <AddList
        productId={view.goal.productId}
        productName={product?.name ?? "this product"}
        mode={adding ? { kind: "new" } : null}
        onClose={() => setAdding(false)}
        onDone={() => void loadLists()}
      />
      {toast}
    </div>
  );
}
